/**
 * Toast notification component.
 * Displays notifications from the store using a simple div-based toast,
 * styled to match CoordioCore's ShellToasterHost appearance.
 */

import { useEffect } from 'react';
import { X as CloseIcon } from 'lucide-react';
import { useNotificationStore } from '../../store/notificationStore';

const SEVERITY_STYLES: Record<string, string> = {
  success: 'border-green-500 bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-200',
  error: 'border-red-500 bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-200',
  warning: 'border-amber-500 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200',
  info: 'border-blue-500 bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200',
};

/**
 * Toast notification component that displays notifications from the store.
 */
export const Toast = () => {
  const { notifications, removeNotification } = useNotificationStore();

  const notification = notifications[0];

  useEffect(() => {
    if (!notification) return;
    const timer = setTimeout(
      () => removeNotification(notification.id),
      notification.duration || 4000,
    );
    return () => clearTimeout(timer);
  }, [notification, removeNotification]);

  if (!notification) {
    return null;
  }

  const severity = notification.type ?? 'info';
  const classes = SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.info;

  return (
    <div className="fixed bottom-6 right-6 z-[1400]" data-testid="toast-notification">
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-lg border-l-4 shadow-lg min-w-[280px] max-w-[420px] ${classes}`}
        role="alert"
        data-testid="toast-notification-alert"
      >
        <span className="flex-1 text-sm">{notification.message}</span>
        <button
          type="button"
          onClick={() => removeNotification(notification.id)}
          className="p-1 rounded hover:opacity-70 transition-opacity"
          aria-label="Close notification"
        >
          <CloseIcon size={16} />
        </button>
      </div>
    </div>
  );
};
